import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Briefcase } from '@phosphor-icons/react';
import { CompanyLogo } from '../../../../../components/CompanyLogo';
import type { SimilarJob } from '../../../../../types/insights';
import { formatSalaryMinMaxPair } from '../../../../../utils/data/format-salary-min-max-pair';
import { trackJobClick } from '../../../../../utils/analytics/analytics';

interface Props {
  similarJobs: SimilarJob[];
  seeAllHref?: string;
}

function formatSalary(job: SimilarJob): string | null {
  const { salaryRangeMin, salaryRangeMax, currency = 'EUR', salaryPeriod } = job;
  if (!salaryRangeMin && !salaryRangeMax) return null;

  const symbol = currency === 'EUR' ? '€' : currency;
  const periodLabel = salaryPeriod === 'annual' ? '/yr' : '/mo';

  if (salaryRangeMin && salaryRangeMax) {
    const body = formatSalaryMinMaxPair(
      salaryRangeMin,
      salaryRangeMax,
      (n) => `${symbol}${n.toLocaleString()}`,
      '–',
    );
    return `${body}${periodLabel}`;
  }
  const value = salaryRangeMin ?? salaryRangeMax ?? 0;
  return `${symbol}${value.toLocaleString()}${periodLabel}`;
}

function formatLocation(location: string | string[] | undefined): string {
  if (!location) return '';
  const primary = Array.isArray(location) ? location[0] : location;
  return primary?.split(',')[0] ?? '';
}

export const SimilarJobsSection: React.FC<Props> = ({ similarJobs, seeAllHref }) => {
  const intl = useIntl();
  if (!similarJobs || similarJobs.length === 0) return null;

  return (
    <section className="mt-6" aria-labelledby="similar-jobs-heading">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <h2
            id="similar-jobs-heading"
            className="text-lg font-semibold text-jc-ink tracking-tight"
          >
            <FormattedMessage
              id="jobDetail.similarJobs.algoliaListTitle"
              defaultMessage="Similar jobs"
              description="Heading for the list of similar job listings from search"
            />
          </h2>
          <div
            className="flex-shrink-0 w-8 h-8 rounded-full bg-jc-accent-muted flex items-center justify-center border border-jc-accent/20 dark:border-jc-accent/35 dark:bg-[#1A2E22]"
            aria-hidden
          >
            <Briefcase className="w-4 h-4 text-jc-cta dark:text-jc-accent" strokeWidth={2} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {seeAllHref ? (
            <a
              href={seeAllHref}
              className="inline-flex items-center rounded-md border border-jc-accent/30 bg-jc-accent-muted/45 px-2 py-1 text-[11px] font-medium text-jc-cta hover:bg-jc-accent-muted/70 transition-colors dark:border-jc-accent/45 dark:bg-[#1A2E22]/90 dark:text-jc-accent dark:hover:bg-[#1A2E22]"
            >
              <FormattedMessage
                id="jobDetail.similarJobs.seeAll"
                defaultMessage="See all"
                description="CTA in similar jobs header to open full search results"
              />
            </a>
          ) : null}
        </div>
      </div>
      <p className="mb-3 text-[11px] text-jc-muted leading-snug">
          <FormattedMessage
            id="jobDetail.similarJobs.sourceNote"
            defaultMessage="From JobCrawls search: same role title and primary location as this listing (this job excluded). Up to 8 results."
            description="Explains that similar jobs are from Algolia using role and location filters"
          />
      </p>

      <ul className="space-y-1.5">
        {similarJobs.map((job) => {
          const salary = formatSalary(job);
          const city = formatLocation(job.location);
          const title = job.normalisedTitle || job.title || '';
          const companyKey = job.companyId || '';

          return (
            <li key={job.objectID}>
              <a
                href={job.url}
                onClick={() =>
                  trackJobClick({
                    jobId: job.objectID,
                    jobTitle: title,
                    companyId: companyKey,
                    source: 'similar_jobs',
                    locale: intl.locale,
                  })
                }
                className="group flex items-center gap-2.5 rounded-xl border border-jc-border bg-jc-page/50 px-2.5 py-2 hover:bg-jc-accent-muted/35 hover:border-jc-accent/35 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-accent/50 focus-visible:ring-offset-2 focus-visible:ring-offset-jc-surface"
              >
                <div className="flex-shrink-0 rounded-xl bg-transparent p-px dark:bg-[#F8F5F0] dark:ring-1 dark:ring-[#98CFA8]/35">
                  <CompanyLogo
                    companyId={companyKey}
                    size={28}
                    variant="rounded"
                    className="shadow-none border-0 dark:!border-0 dark:!bg-transparent"
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-jc-ink group-hover:text-jc-cta dark:group-hover:text-jc-accent transition-colors leading-tight truncate">
                    {title}
                  </p>
                  <div className="mt-0.5 flex items-center gap-1 text-[11px] text-jc-muted">
                    {companyKey && <span className="truncate">{companyKey}</span>}
                    {companyKey && city && <span aria-hidden>·</span>}
                    {city && <span className="truncate">{city}</span>}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {salary && (
                    <span className="text-[11px] font-medium tabular-nums text-jc-gold bg-jc-gold-bg/60 border border-jc-gold-line/20 px-1.5 py-0.5 rounded-md leading-none whitespace-nowrap">
                      {salary}
                    </span>
                  )}
                  <span
                    className="w-6 h-6 rounded-full bg-jc-elevated border border-jc-border flex items-center justify-center text-jc-accent group-hover:bg-jc-accent-muted group-hover:border-jc-accent/25 transition-colors"
                    aria-hidden
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
